import { normalizeLabelCode } from "./codes";

/*
 * Where a label points (ADR 0018 › Where a label points): the installation's own
 * address, `<BT_PUBLIC_URL uppercased>/QR/<CODE>`, as a version 3 QR code in alphanumeric mode.
 */

/** What a version 3 QR code holds in alphanumeric mode, by error correction level. */
const CAPACITY = { Q: 47, M: 61 } as const;
export type LabelErrorCorrection = keyof typeof CAPACITY;

/** The characters alphanumeric mode can encode: uppercase is what makes it possible. */
const ALPHANUMERIC = /^[0-9A-Z $%*+\-./:]*$/;

/** The address a label encodes, e.g. `HTTPS://APP.BIMMERTOOLS.DE/QR/K2W2T8M`. */
export function labelUrl(publicUrl: string, code: string): string {
  return `${publicUrl.replace(/\/+$/, "").toUpperCase()}/QR/${code}`;
}

/**
 * The error correction a label's address gets at the one size every label has: Q (25 % recovery,
 * for oil and scratches) up to 47 characters, M up to 61. A longer address, or one with characters
 * alphanumeric mode can't encode (say `_` or `~` in a self-hosted path), can't be printed.
 */
export function labelErrorCorrection(
  url: string,
):
  | { ok: true; level: LabelErrorCorrection }
  | { ok: false; error: "LABEL_URL_TOO_LONG" | "LABEL_URL_INVALID" } {
  if (!ALPHANUMERIC.test(url)) return { ok: false, error: "LABEL_URL_INVALID" };
  if (url.length <= CAPACITY.Q) return { ok: true, level: "Q" };
  if (url.length <= CAPACITY.M) return { ok: true, level: "M" };
  return { ok: false, error: "LABEL_URL_TOO_LONG" };
}

/**
 * What a path below /qr is, for the server's middleware and the web app's guard:
 * - `none`: not below /qr;
 * - `canonical`: `/qr` (the scanner of the garage used last) or `/qr/<CODE>`;
 * - `redirect`: another spelling of one of them (`/QR`, `/qr/`, `/qr/k2w-2t8m`), to `to`;
 * - `invalid`: no code even after normalising, to be sent to `/qr?invalid=<raw>`.
 */
export type QrPath =
  | { kind: "none" }
  | { kind: "canonical"; code: string | null }
  | { kind: "redirect"; to: string }
  | { kind: "invalid"; raw: string; to: string };

/** Reads a request's raw path (still percent-encoded, without its query). */
export function qrPath(rawPath: string): QrPath {
  const match = /^\/qr(?:\/(.*))?$/i.exec(rawPath);
  if (!match) return { kind: "none" };
  const encoded = match[1] ?? "";
  if (encoded === "" || encoded === "/") {
    return rawPath === "/qr" ? { kind: "canonical", code: null } : { kind: "redirect", to: "/qr" };
  }
  let raw = encoded;
  try {
    raw = decodeURIComponent(encoded);
  } catch {
    // Not valid percent-encoding: the raw text is what the user gets told about.
  }
  // One trailing slash is a spelling; anything else after the code isn't a code.
  const code = normalizeLabelCode(raw.replace(/\/$/, ""));
  if (!code) return { kind: "invalid", raw, to: `/qr?invalid=${encodeURIComponent(raw)}` };
  const canonical = `/qr/${code}`;
  return rawPath === canonical ? { kind: "canonical", code } : { kind: "redirect", to: canonical };
}

/**
 * The code in what the scanner read or someone typed: a label's address on any host, in any case,
 * with or without its scheme, or a bare code. Null when it is no label.
 */
export function parseScannedText(text: string): string | null {
  const trimmed = text.trim();
  const url = /^(?:[a-z][a-z0-9+.-]*:\/\/)?[^/\s]+\/qr\/([^/?#\s]+)\/?(?:[?#]\S*)?$/i.exec(trimmed);
  if (url?.[1]) {
    let raw = url[1];
    try {
      raw = decodeURIComponent(raw);
    } catch {
      return null;
    }
    return normalizeLabelCode(raw);
  }
  return normalizeLabelCode(trimmed);
}
